import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { getImageUrl } from '@/utils/imagePaths';
import { ArrowLeftIcon, PhotoIcon, XMarkIcon, LinkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import ProviderCombobox from '@/components/ProviderCombobox';
import { getAppSocket } from '@/lib/wesSocket';

type Supply = {
  id: string;
  name: string;
  description?: string;
  category?: string;
  unit: string;
  stock: number;
  minStock: number;
  location?: string;
  costPrice: number;
  supplier?: string;
  attachmentUrl?: string;
  isActive: number;
  areaId?: string;
};

type Area = {
  id: string;
  name: string;
};

type Provider = {
  id: string;
  name: string;
  businessName?: string;
};

type SupplyCategory = {
  id: string;
  name: string;
};

type ServiceLink = {
  id: string;
  serviceProductId: string;
  serviceName: string;
};

type ServiceOption = {
  id: string;
  name: string;
};

const LOCATIONS = [
  'Depósito principal',
  'Depósito secundario',
  'Taller',
  'Mostrador',
  'Oficina',
  'Estantería A',
  'Estantería B',
  'Estantería C',
  'Pañol'
];

const emptySupply: Supply = {
  id: '',
  name: '',
  description: '',
  category: '',
  unit: '',
  stock: 0,
  minStock: 0,
  location: '',
  costPrice: 0,
  supplier: '',
  attachmentUrl: '',
  isActive: 1,
  areaId: ''
};

const SupplyFormPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const supplyFromState = (location.state as { supply?: Supply } | undefined)?.supply;
  const isEdit = Boolean(id);

  const [form, setForm] = useState<Supply>(emptySupply);
  const [isDirty, setIsDirty] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [categories, setCategories] = useState<SupplyCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [extraUnits, setExtraUnits] = useState<string[]>([]);
  useEffect(() => {
    const known = ['unidad', 'litro', 'kg', 'metro', 'caja', 'paquete', 'par', 'juego'];
    axios.get('/api/products/units')
      .then(r => setExtraUnits((r.data?.units || []).filter((u: string) => u && !known.includes(u))))
      .catch(() => {});
  }, []);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSupply, setLoadingSupply] = useState<boolean>(Boolean(isEdit && !supplyFromState));
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Service links
  const [serviceLinks, setServiceLinks] = useState<ServiceLink[]>([]);
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  // Pending links for creation mode (stored locally until supply is saved)
  const [pendingLinks, setPendingLinks] = useState<ServiceOption[]>([]);

  // Provider combobox
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');
  const [newProviderPhone, setNewProviderPhone] = useState('');
  const [creatingProvider, setCreatingProvider] = useState(false);

  const fetchProvidersList = useCallback(async () => {
    try {
      const res = await axios.get('/api/providers', { params: { page: 1, limit: 200, isActive: true } });
      setProviders(res.data.providers || []);
    } catch (error: any) {
      console.error('Error fetching providers', error);
    }
  }, []);

  const fetchAreasList = useCallback(async () => {
    try {
      const res = await axios.get('/api/settings/areas');
      setAreas(res.data.areas || []);
    } catch (error: any) {
      console.error('Error fetching areas', error);
    }
  }, []);

  const fetchCategoriesList = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const res = await axios.get('/api/settings/supply-categories');
      setCategories(res.data?.categories || []);
    } catch (error: any) {
      console.error('Error fetching supply categories', error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const fetchServiceLinks = useCallback(async (supplyId: string) => {
    try {
      const res = await axios.get(`/api/supplies/${supplyId}/service-links`);
      setServiceLinks(res.data.links || []);
    } catch (error: any) {
      console.error('Error fetching service links', error);
    }
  }, []);

  const fetchServiceOptions = useCallback(async () => {
    try {
      const res = await axios.get('/api/products', { params: { category: 'servicio', limit: 200, isActive: true } });
      const products = res.data.products || res.data || [];
      setServiceOptions(products.map((p: any) => ({ id: p.id, name: p.name })));
    } catch (error: any) {
      console.error('Error fetching services', error);
    }
  }, []);

  const addServiceLink = async () => {
    if (!selectedServiceId || !id) return;
    setAddingLink(true);
    try {
      await axios.post('/api/supplies/service-links', { supplyId: id, serviceProductId: selectedServiceId });
      toast.success('Servicio vinculado');
      setSelectedServiceId('');
      fetchServiceLinks(id);
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('Este servicio ya está vinculado');
      } else {
        toast.error('Error al vincular servicio');
      }
    } finally {
      setAddingLink(false);
    }
  };

  const removeServiceLink = async (linkId: string) => {
    try {
      await axios.delete(`/api/supplies/service-links/${linkId}`);
      toast.success('Vínculo eliminado');
      if (id) fetchServiceLinks(id);
    } catch (error: any) {
      toast.error('Error al eliminar vínculo');
    }
  };

  // Pending links for creation mode
  const addPendingLink = () => {
    if (!selectedServiceId) return;
    const svc = serviceOptions.find(s => s.id === selectedServiceId);
    if (!svc) return;
    if (pendingLinks.some(p => p.id === selectedServiceId)) {
      toast.error('Este servicio ya está vinculado');
      return;
    }
    setPendingLinks(prev => [...prev, svc]);
    setSelectedServiceId('');
  };

  const removePendingLink = (serviceId: string) => {
    setPendingLinks(prev => prev.filter(p => p.id !== serviceId));
  };

  // Create provider inline
  const handleCreateProvider = async () => {
    if (!newProviderName.trim()) {
      toast.error('El nombre del proveedor es obligatorio');
      return;
    }
    setCreatingProvider(true);
    try {
      const res = await axios.post('/api/providers', {
        name: newProviderName.trim(),
        type: 'company',
        phone: newProviderPhone.trim() || undefined
      });
      const created = res.data?.provider || res.data;
      toast.success('Proveedor creado');
      await fetchProvidersList();
      if (created?.name) {
        updateForm({ supplier: created.name });
        setSelectedProvider({ id: created.id, name: created.name, businessName: created.businessName });
      }
      setProviderModalOpen(false);
      setNewProviderName('');
      setNewProviderPhone('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear proveedor');
    } finally {
      setCreatingProvider(false);
    }
  };

  useEffect(() => {
    fetchProvidersList();
    fetchAreasList();
    fetchCategoriesList();
    fetchServiceOptions();
  }, [fetchProvidersList, fetchAreasList, fetchCategoriesList, fetchServiceOptions]);

  useEffect(() => {
    if (isEdit && id) fetchServiceLinks(id);
  }, [isEdit, id, fetchServiceLinks]);

  // Sync selectedProvider when form.supplier or providers change
  useEffect(() => {
    if (form.supplier && providers.length > 0) {
      const found = providers.find(p => p.name === form.supplier || p.businessName === form.supplier);
      setSelectedProvider(found || null);
    } else {
      setSelectedProvider(null);
    }
  }, [form.supplier, providers]);

  const fetchSupplyById = useCallback(async (supplyId: string) => {
    try {
      setLoadingSupply(true);
      let supply: Supply | null = null;
      try {
        const res = await axios.get(`/api/supplies/${supplyId}`);
        supply = res.data?.supply || res.data || null;
      } catch (error: any) {
        // Si el endpoint individual no existe, intentar traer el listado y buscar el insumo
        const listRes = await axios.get('/api/supplies', { params: { page: 1, limit: 200 } });
        supply =
          (listRes.data.supplies || []).find(
            (item: Supply) => String(item.id) === String(supplyId)
          ) || null;
      }

      if (!supply) {
        toast.error('No se encontró el insumo');
        navigate('/supplies');
        return;
      }

      setForm(supply);
      setImagePreview(supply.attachmentUrl || null);
      setIsDirty(false);
    } catch (error: any) {
      console.error('Error loading supply', error);
      toast.error(error.response?.data?.error || 'No se pudo cargar el insumo');
      navigate('/supplies');
    } finally {
      setLoadingSupply(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isEdit) {
      setForm(emptySupply);
      setImagePreview(null);
      setLoadingSupply(false);
      setIsDirty(false);
      return;
    }
    if (supplyFromState) {
      setForm(supplyFromState);
      setImagePreview(supplyFromState.attachmentUrl || null);
      setLoadingSupply(false);
      setIsDirty(false);
    } else if (id) {
      fetchSupplyById(id);
    }
  }, [isEdit, id, supplyFromState, fetchSupplyById]);

  const updateForm = useCallback((patch: Partial<Supply>) => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post('/api/supplies/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.imageUrl) {
        setImagePreview(res.data.imageUrl);
        updateForm({ attachmentUrl: res.data.imageUrl });
        toast.success('Imagen subida correctamente');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.error || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    updateForm({ attachmentUrl: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  const notifiedRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/supplies') ||
          path.startsWith('/api/settings/areas') ||
          path.startsWith('/api/providers') ||
          path.startsWith('/api/settings/supply-categories')
        )
      ) {
        return;
      }

      if (isDirty || saving || uploadingImage || loadingSupply) {
        pendingRefreshRef.current = true;
        if (!notifiedRef.current && isDirty) {
          notifiedRef.current = true;
          toast.message('Se detectaron cambios externos. Se actualizará al guardar o descartar cambios.');
        }
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchProvidersList();
        fetchAreasList();
        fetchCategoriesList();
        if (isEdit && id) {
          fetchSupplyById(id);
        }
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchProvidersList, fetchAreasList, fetchCategoriesList, fetchSupplyById, id, isEdit, isDirty, saving, uploadingImage, loadingSupply]);

  useEffect(() => {
    if (isDirty || saving || uploadingImage || loadingSupply) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    notifiedRef.current = false;
    fetchProvidersList();
    fetchAreasList();
    fetchCategoriesList();
    if (isEdit && id) {
      fetchSupplyById(id);
    }
  }, [fetchProvidersList, fetchAreasList, fetchCategoriesList, fetchSupplyById, id, isEdit, isDirty, saving, uploadingImage, loadingSupply]);

  const saveSupply = async () => {
    if (!form.name || !form.unit) {
      toast.error('Nombre y unidad son obligatorios');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await axios.put(`/api/supplies/${id}`, form);
        toast.success('Insumo actualizado');
      } else {
        const createRes = await axios.post('/api/supplies', form);
        const newSupply = createRes.data?.supply || createRes.data;
        // Create pending service links
        if (newSupply?.id && pendingLinks.length > 0) {
          for (const pl of pendingLinks) {
            try {
              await axios.post('/api/supplies/service-links', { supplyId: newSupply.id, serviceProductId: pl.id });
            } catch (linkErr) {
              console.error('Error creating link:', linkErr);
            }
          }
        }
        toast.success('Insumo creado');
      }
      navigate('/supplies');
    } catch (error: any) {
      console.error('Error saving supply', error);
      toast.error(error.response?.data?.error || 'No se pudo guardar el insumo');
    } finally {
      setSaving(false);
    }
  };

  if (!hasPermission('supplies')) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        No tienes permisos para gestionar insumos.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/supplies')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {isEdit ? 'Editar insumo' : 'Nuevo insumo'}
              </h1>
              <p className="text-sm text-muted-foreground">
                Completa los datos del insumo y guarda los cambios.
              </p>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="py-6">
            {loadingSupply ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => updateForm({ name: e.target.value })}
                    placeholder="Nombre del insumo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidad *</Label>
                  <Select
                    value={form.unit}
                    onValueChange={(value) => updateForm({ unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar unidad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unidad">Unidad</SelectItem>
                      <SelectItem value="litro">Litro</SelectItem>
                      <SelectItem value="kg">Kilogramo</SelectItem>
                      <SelectItem value="metro">Metro</SelectItem>
                      <SelectItem value="caja">Caja</SelectItem>
                      <SelectItem value="paquete">Paquete</SelectItem>
                      <SelectItem value="par">Par</SelectItem>
                      <SelectItem value="juego">Juego</SelectItem>
                      {extraUnits.map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Stock actual</Label>
                  <Input
                    type="number"
                    value={form.stock}
                    min={0}
                    onChange={(e) => updateForm({ stock: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stock mínimo</Label>
                  <Input
                    type="number"
                    value={form.minStock}
                    min={0}
                    onChange={(e) => updateForm({ minStock: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría (área)</Label>
                  <Select
                    value={form.category || ''}
                    onValueChange={(value) => {
                      const area = areas.find((a) => a.name === value);
                      updateForm({ category: value, areaId: area ? area.id : '' });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar área" />
                    </SelectTrigger>
                    <SelectContent>
                      {areas.length ? (
                        areas.map((area) => (
                          <SelectItem key={area.id} value={area.name}>
                            {area.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-areas" disabled>
                          No hay áreas creadas
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Proveedor</Label>
                  <ProviderCombobox
                    providers={providers}
                    selectedProvider={selectedProvider}
                    onSelect={(provider) => {
                      if (provider) {
                        updateForm({ supplier: provider.name });
                        setSelectedProvider(provider);
                      } else {
                        updateForm({ supplier: '' });
                        setSelectedProvider(null);
                      }
                    }}
                    onCreateProvider={(query) => {
                      setNewProviderName(query);
                      setProviderModalOpen(true);
                    }}
                    placeholder="Buscar proveedor..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ubicación</Label>
                  <Select
                    value={form.location || ''}
                    onValueChange={(value) => updateForm({ location: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar ubicación" />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATIONS.map((loc) => (
                        <SelectItem key={loc} value={loc}>
                          {loc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Costo promedio</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.costPrice}
                    onChange={(e) => updateForm({ costPrice: Number(e.target.value) })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Descripción</Label>
                  <Input
                    value={form.description || ''}
                    onChange={(e) => updateForm({ description: e.target.value })}
                    placeholder="Descripción opcional del insumo"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Imagen del insumo</Label>
                  <div className="flex items-start gap-4">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={getImageUrl(imagePreview)}
                          alt="Preview"
                          className="w-32 h-32 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-32 h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-accent/50 transition-colors"
                      >
                        <PhotoIcon className="h-8 w-8 text-muted-foreground mb-2" />
                        <span className="text-xs text-muted-foreground text-center px-2">
                          {uploadingImage ? 'Subiendo...' : 'Click para subir'}
                        </span>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    <div className="text-xs text-muted-foreground">
                      <p>Formatos: JPG, PNG, GIF, WEBP</p>
                      <p>Tamaño máximo: 5MB</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 border-t pt-6">
              <div className="flex items-center gap-2 mb-4">
                <LinkIcon className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Servicios vinculados
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Vinculá este insumo con los servicios que lo consumen para ver estadísticas de rendimiento en el reporte de eficiencia.
              </p>

              {/* Edit mode: actual persisted links */}
              {isEdit && serviceLinks.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {serviceLinks.map((link) => (
                    <Badge key={link.id} variant="secondary" className="flex items-center gap-1.5 py-1.5 px-3 text-sm">
                      {link.serviceName}
                      <button
                        type="button"
                        onClick={() => removeServiceLink(link.id)}
                        className="ml-1 hover:text-destructive transition-colors"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Create mode: pending links in local state */}
              {!isEdit && pendingLinks.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {pendingLinks.map((pl) => (
                    <Badge key={pl.id} variant="secondary" className="flex items-center gap-1.5 py-1.5 px-3 text-sm">
                      {pl.name}
                      <button
                        type="button"
                        onClick={() => removePendingLink(pl.id)}
                        className="ml-1 hover:text-destructive transition-colors"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Seleccionar servicio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceOptions
                      .filter((s) =>
                        isEdit
                          ? !serviceLinks.some((l) => l.serviceProductId === s.id)
                          : !pendingLinks.some((p) => p.id === s.id)
                      )
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={isEdit ? addServiceLink : addPendingLink}
                  disabled={!selectedServiceId || addingLink}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Vincular
                </Button>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate('/supplies')}>
                Cancelar
              </Button>
              <Button onClick={saveSupply} disabled={saving || uploadingImage}>
                {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear insumo'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal crear proveedor */}
      <Dialog open={providerModalOpen} onOpenChange={setProviderModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear proveedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={newProviderName}
                onChange={(e) => setNewProviderName(e.target.value)}
                placeholder="Nombre del proveedor"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={newProviderPhone}
                onChange={(e) => setNewProviderPhone(e.target.value)}
                placeholder="Teléfono (opcional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProviderModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateProvider} disabled={creatingProvider || !newProviderName.trim()}>
              {creatingProvider ? 'Creando...' : 'Crear proveedor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplyFormPage;
